import {
    wordParentWeightIndex,
    type DisplayWord,
    type LineClass,
    type ReferenceWeight,
    Sentence,
    weigh,
} from "@circleofa/acim-engine"
import { WordBox, WordBoxRect, type EnhancedRendererRect, type WordSelectionPoint } from "./wordbox"
import { getMaxValue, getMaxValues } from "@utils/helpers/array-helpers"
import * as Geometry from "./word-geometry-helpers"


/**
 * {@throws no lines found if there are no lines}
 */
export class LineHelpers {
    public readonly lineHelpers: LineHelper[]
    wordRects: WordBoxRect[] = []
    wordBoxes: WordBox[] = []
    constructor(
        spans: HTMLSpanElement[],
        public readonly offsetParent: HTMLElement,
    ) {
        if (!spans.length) throw new Error("No line spans found!")
        this.lineHelpers = spans.map((span, i) => new LineHelper(span, this, i))

        // Boxes are already generated
        // this.consolidateBoxes()
    }

    // /**
    //  * Retrieve the lineHelper for the associated line
    //  */
    // lineHelper(line : LineClass) : LineHelper {
    //     return this.lineHelpers.find(helper => helper.line === line)!
    // }

    // getRange(start : WordBox) {

    // }

    /**
     * Retrieves the word that's "prior". Will not return any word
     * that the pointer is directly over.
     * @param offsetX
     * @param offsetY
     */
    priorWord(offsetX : number, offsetY : number) : WordBox | null {
      const rtn =  Geometry.prior({x: offsetX, y: offsetY}, this.wordRects)
      if (rtn) return rtn.wordBox
      return null
      // // Check to see if we're vertically inside
      // const verticallyInside = this.wordRects.filter(rect => rect.isVerticallyInside(offsetY))
      // if (verticallyInside.length) {
      //   const horizontal
      // } else {
      //   // Not vertically inside, check above
      //   const above = this.wordRects.filter((rect) => rect.isAbove(offsetY))
      // }
      // if (!aboveOrVerticallyInside.length) return null

      // const horizontallyLeft = aboveOrVerticallyInside.filter(rect => rect.right > offsetX)
      // if (!horizontallyLeft.length) return null
      // return horizontallyLeft[horizontallyLeft.length - 1]!.wordBox
    }

    /**
     * Retrieves the word that's "next" or directly under the pointer
     * @param offsetX
     * @param offsetY
     */
    nextWord(offsetX : number, offsetY : number) : WordBox | null {
      const rtn =  Geometry.next({x: offsetX, y: offsetY}, this.wordRects)
      if (rtn) return rtn.wordBox
      return null
      // const aboveOrVerticallyInside = this.wordRects.filter(rect => rect.y < offsetY)
      // const horizontallyLeft = aboveOrVerticallyInside.filter(rect => rect > offsetX)
      // if (!horizontallyLeft.length) return null
      // return horizontallyLeft[horizontallyLeft.length - 1]!.wordBox
    }

    closestWord(offsetX: number, offsetY: number): WordSelectionPoint {
        return {
          next: this.nextWord(offsetX, offsetY),
          prev: this.priorWord(offsetX, offsetY)
        }
        // const aboveOrVerticallyInside = this.wordRects.filter(
        //     (rect) => rect.isAbove(offsetY) || rect.isVerticallyInside(offsetY),
        // )
        // const verticallyInsideAndLeft = aboveOrVerticallyInside
        //     .filter((rect) => rect.isVerticallyInside(offsetY))
        //     .filter((rect) => rect.toTheLeftOf(offsetX))

        // if (verticallyInsideAndLeft.length) {
        //     return getMaxValue(verticallyInsideAndLeft, (rect) => rect.x)!.wordBox
        // }

        // const above = aboveOrVerticallyInside.filter((rect) => rect.isAbove(offsetY))
        // if (above.length) {
        //     const closestLineAbove = getMaxValues(above, (rect) => rect.y, 1)
        //     return getMaxValue(closestLineAbove, (rect) => rect.x)?.wordBox ?? null
        // }

        // if (this.wordRects[0].isBelow(offsetY)) return this.wordRects[0].wordBox

        // return null
    }

    consolidateBoxes() {
        this.wordBoxes = this.lineHelpers.flatMap((lineHelper) => lineHelper.boxes)
        this.wordRects = this.wordBoxes.flatMap((box) => box.rects)
    }

    generateBoxes(offsetX: number, offsetY: number) {
        const range = new Range()
        this.wordRects = this.lineHelpers.flatMap((lineHelper) => {
            return lineHelper.generateBoxes(offsetX, offsetY, range)
        })
        this.consolidateBoxes()
    }

    get firstWord() {
        return this.lineHelpers[0].firstWord
    }

    get lastWord() {
        return this.lineHelpers[this.lineHelpers.length - 1].lastWord
    }
}

/**
 * Responsible for giving convenient APIs for individual lines.
 * For example finding weights, character offsets for words, etc
 */
export class LineHelper {
    public readonly renderingWords: DisplayWord[]
    public readonly line: LineClass
    public readonly childNodes: ChildNode[]
    /**
     * Each DOMRect[] corresponds to the index of a word
     */
    #boxes: WordBox[] = []
    #sentenceNumberRenderer: EnhancedRendererRect[] = []
    constructor(
        public readonly element: HTMLSpanElement,
        public readonly container: LineHelpers,
        public readonly lineIndex: number,
    ) {
        const startingWeight = Number(element.dataset.startingWeight)
        const wordParent = wordParentWeightIndex.findFromWordWeight(startingWeight)

        let line: LineClass
        if (wordParent instanceof Sentence) {
            if (wordParent.lines.length === 1) {
                line = wordParent.lines[0]
            } else {
                const lineNumber = Number(element.dataset.line)
                line = wordParent.lines[lineNumber]
            }
        } else {
            line = wordParent
        }

        if (!line) throw new RenderLineNotFoundError(startingWeight)
        this.childNodes = [...element.childNodes]
        this.line = line
        const renderingWords = line.displayWords
        if (renderingWords === undefined) throw new RenderWeightNotFoundError(startingWeight)
        if (!renderingWords.length) throw new NoWordsFoundError(startingWeight)
        this.renderingWords = renderingWords
        // this.generateBoxes()
    }

    get sentenceNumberRect(): EnhancedRendererRect[] {
        return this.#sentenceNumberRenderer
    }

    get firstWord() {
        return this.renderingWords[0]
    }

    get lastWord() {
        return this.renderingWords[this.renderingWords.length - 1]
    }

    get boxes() {
        return this.#boxes
    }

    /**
     * This gets the {@link EnhancedRendererRect} for the sentence number
     * in case we want to show it in the highlights
     */
    #generateSentenceNumberRenderer(offsetX: number, offsetY: number) {
        // Let's get the sentence number
        if (this.#boxes[0].word.nodeIndex === 1) {
            // The first box is not the first child. The likely case is that
            // the sentence number is then the first child
            const child = this.element.firstChild
            if (child instanceof HTMLElement) {
                const rects = [...child.getClientRects()].map(
                    ({ x, right, y, bottom, width, height }) => ({
                        x: x - offsetX,
                        right: right - offsetX,
                        width,
                        height,
                        y: y - offsetY,
                        bottom: bottom - offsetY,
                    }),
                )
                this.#sentenceNumberRenderer = rects
            }
        }
    }

    /**
     *
     * @param range pass a range to save performance costs of re-initialisation
     */
    generateBoxes(offsetX: number, offsetY: number, range = new Range()): WordBoxRect[] {
        const rects: WordBoxRect[] = []
        this.#boxes = this.renderingWords.flatMap((renderingWord, i) => {
            const { startOffset, endOffset, span, nodeIndex } = renderingWord
            try {
                const node = this.childNodes[nodeIndex]
                if (span) {
                    range.selectNode(node)
                } else {
                    if (startOffset === undefined || endOffset === undefined) {
                        console.error(renderingWord)
                        throw new Error("One or both offsets was not set!")
                    }
                    range.setStart(node, startOffset)
                    range.setEnd(node, endOffset)
                }
                const wordBox = new WordBox(this, i, [...range.getClientRects()], offsetX, offsetY)
                rects.push(...wordBox.rects)
                return wordBox
            } catch (e) {
                console.error(renderingWord)
                console.log({ nodeIndex, childNodes: this.childNodes })
                throw e
            }
        })
        this.#generateSentenceNumberRenderer(offsetX, offsetY)
        return rects
    }
}

export class RenderWeightNotFoundError extends Error {
    constructor(number: number) {
        super(`The following rendering line could not be found from weight: ${number}`)
        this.name = "RenderWeightNotFoundError"
        Object.setPrototypeOf(this, RenderWeightNotFoundError.prototype)
    }
}

export class RenderLineNotFoundError extends Error {
    constructor(number: ReferenceWeight) {
        super(`The following word data could not be found from line starting weight: ${number}`)
        this.name = "RenderWeightNotFoundError"
        Object.setPrototypeOf(this, RenderWeightNotFoundError.prototype)
    }
}

export class NoWordsFoundError extends Error {
    constructor(number: ReferenceWeight) {
        super(`No words were found in the following: ${number}`)
        this.name = "RenderWeightNotFoundError"
        Object.setPrototypeOf(this, RenderWeightNotFoundError.prototype)
    }
}
